// DEV-07「複製到其他通道」：把一路的設定值寫到其他通道（走各通道自己的 shadow desired），再逐路回讀確認。
import type { DeviceSettings, SettingItem } from '../../lib/deviceApi';
import { itemLabel } from '../../lib/kinioLabel';
import { inTiers, type Tier } from './settingsTiers';

export interface CopyChange {
  key: string;
  label: string;
  value: unknown;
}

export function flatItems(s: DeviceSettings | undefined): SettingItem[] {
  return (s?.sections ?? []).flatMap((sec) => sec.clusters.flatMap((c) => c.items));
}

/** 能不能複製：可寫、有值、不是指令 / 自訂編輯器 / 唯讀，也不是破壞性的項目。 */
export function copyable(it: SettingItem): boolean {
  return (
    it.can_write &&
    !it.destructive &&
    it.value !== undefined &&
    it.value !== null &&
    it.control !== 'readout' &&
    it.control !== 'button' &&
    it.control !== 'custom'
  );
}

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/** 來源 → 目標要寫的項目：兩邊都有、目標可寫、值不同。only = 只複製這些 key（沒給 = 全部）；tiers = 只複製這些層（沒給 = 全部）。 */
export function copyChanges(source: SettingItem[], target: SettingItem[], only?: Set<string>, tiers?: ReadonlySet<Tier>): CopyChange[] {
  const tgt = new Map(target.map((t) => [t.key, t]));
  const out: CopyChange[] = [];
  for (const s of source) {
    if (!copyable(s) || (only && !only.has(s.key)) || (tiers && !inTiers(s, tiers))) continue;
    const t = tgt.get(s.key);
    if (!t || !copyable(t) || same(s.value, t.value)) continue;
    out.push({ key: s.key, label: itemLabel(s), value: s.value });
  }
  return out;
}

/** 變更 → shadow desired 的 state（cluster → attr → value）。 */
export function toState(changes: CopyChange[]): Record<string, Record<string, unknown>> {
  const st: Record<string, Record<string, unknown>> = {};
  for (const c of changes) {
    const [cluster, attr] = c.key.split('.');
    (st[cluster] ??= {})[attr] = c.value;
  }
  return st;
}

/** 回讀：目標目前的值跟要寫的還不一樣的 key（空 = 全部一致）。 */
export function mismatches(changes: CopyChange[], target: SettingItem[]): string[] {
  const tgt = new Map(target.map((t) => [t.key, t]));
  return changes.filter((c) => !same(tgt.get(c.key)?.value, c.value)).map((c) => c.key);
}

/** 回讀：還在 desired（設備還沒 ack）的 key。空了才看 mismatches——設備 ack 後以回報值為準（SPEC #shadow-sync）。 */
export function stillPending(changes: CopyChange[], target: SettingItem[]): string[] {
  const tgt = new Map(target.map((t) => [t.key, t]));
  return changes.filter((c) => tgt.get(c.key)?.pending !== false).map((c) => c.key);
}
